import React, { useCallback, useEffect, useState } from 'react';
import KakaoIcon from '../components/KakaoIcon';
import { useScrollLockClass } from '../hooks/useScrollLockClass';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../hooks/useBackHandler';
import { getRelativeTime } from '../utils/date';
import BackButton from '../components/BackButton';
import PhotoAlbum from '../components/PhotoAlbum';

/**
 * **공지 탭**(사용자 2026-10-27). 읽기는 모두, 쓰기·고치기·지우기·고정은 길마·부길마(서버 `routes/notice` 가 막는다).
 *
 *   목록은 게시판 줄(번호 · 제목 · 작성자 · 작성일, 사용자 2026-10-27 초안 2) — 눌러야 본문·그림이 보인다
 *   고정 공지는 맨 위(번호 대신 '공지'), 그다음 최근 순
 *   새 글에는 빨간 N — 올라온 지 하루가 안 됐거나 마지막으로 탭을 연 뒤 올라온 글(카페처럼, 쓴 사람에게도 보인다)
 *   탭을 열면 서버에 "읽음"을 남기고 탭의 빨간 점을 끈다(`swpvpdb:notice-seen`)
 *   새 공지는 카톡 봇이 알린다(어느 방인지는 [설정] 탭의 '사이트 공지 알람')
 */
interface Notice {
  _id: string;
  title: string;
  body: string;
  pinned: boolean;
  authorName: string;
  authorProfileImg: string | null;
  images: NoticeImage[];
  createdAt: string;
  updatedAt: string;
}

/** kakao — 새 공지를 카톡 봇으로 알릴지(글마다 켜고 끈다, 사용자 2026-10-27). 고칠 때는 다시 알리지 않는다 */
interface Draft { id: string | null; title: string; body: string; pinned: boolean; kakao: boolean; images: NoticeImage[] }

/** 공지 그림 — 서버가 준 파일 이름과 주소(`/images/notice/…`) */
interface NoticeImage { name: string; url: string }

const IMG_MAX = 10;

/**
 * 목록·본문 글자색(사용자 2026-10-27 초안: 라이트 1번 진한 남색 · 다크 7번 어두운 카드).
 * 다크모드 자동 칠하기(utils/theme)가 이 두 카드를 건드리지 않게 `data-dark-skip` 을 달고 여기서 직접 고른다.
 */
const PALETTE = {
  light: {
    card: 'rgba(255, 255, 255, 0.95)', head: '#8a90a8', headLine: '#3b4a8a', pinBg: '#f4f6fc', line: '#e8ebf5',
    title: '#1f2a55', name: '#4b5375', sub: '#9aa0b5', badge: '#3b4a8a', body: '#2f3654',
    menu: 'rgba(255, 255, 255, 0.97)', menuText: '#333', more: '#666'
  },
  dark: {
    // 바탕·줄은 공략 목록이 다크모드에서 칠해지는 색과 같게(사용자 2026-10-30 "공략 게시글 배경처럼") —
    // 흰 판 → #2c3248(theme.ts), 줄 → 공략과 같은 반투명 보라
    card: '#2c3248', head: '#8a8fb5', headLine: 'rgba(102, 126, 234, 0.45)', pinBg: '#363d57', line: 'rgba(102, 126, 234, 0.3)',
    title: '#f1efff', name: '#c8c4ec', sub: '#8f8bb8', badge: '#8b80ff', body: '#dcd8f5',
    menu: '#2a2745', menuText: '#f1efff', more: '#c8c4ec'
  }
};

/** 고정 압정 — 기울어진 압정(사용자 2026-10-27 초안 2). 고정이면 꽉 채우고, 아니면 선만 */
function PinIcon({ on, color }: { on: boolean; color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <g transform="rotate(35 12 12)">
        <path
          d="M8 3h8l-1.5 2v5l3 3H6.5l3-3V5z"
          fill={on ? color : 'none'}
          stroke={on ? 'none' : color}
          strokeWidth={1.6}
          strokeLinejoin="round"
        />
        <path d="M12 13v8" stroke={color} strokeWidth={on ? 2 : 1.8} strokeLinecap="round" />
      </g>
    </svg>
  );
}

/** 다크모드인지 — 프로필 창에서 바꾸면 바로 따라간다(<html data-theme>) */
function useDarkTheme(): boolean {
  const [dark, setDark] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');
  useEffect(() => {
    const ob = new MutationObserver(() => setDark(document.documentElement.getAttribute('data-theme') === 'dark'));
    ob.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => ob.disconnect();
  }, []);
  return dark;
}

/** 새 글 N 을 다는 기간 — 이 안에 올라온 글은 읽었어도 N(사용자 2026-10-27 "New 도 같이 표시") */
const NEW_FOR_MS = 24 * 3600 * 1000;

/** 목록 날짜 — 오늘이면 시각, 아니면 월.일 */
function shortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  if (d.toDateString() === now.toDateString()) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  return `${p(d.getMonth() + 1)}.${p(d.getDate())}`;
}

const PURPLE = '#667eea';

const NoticePage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [notices, setNotices] = useState<Notice[] | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  /** 이번에 열기 전의 "읽은 때" — [NEW] 는 이것보다 뒤에 올라온 공지 */
  const [seenAt, setSeenAt] = useState<number>(0);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  /** 펼쳐 읽는 공지 — 카톡 링크(/notice/<id>)로 들어왔으면 그 글부터 */
  const [readingId, setReadingId] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/notice\/([a-f0-9]{24})$/);
    if (!m) return null;
    window.history.replaceState({}, document.title, '/');
    return m[1];
  });
  /** 공지 화면의 ⋮ 메뉴(수정·삭제) — 바깥을 누르면 닫는다 */
  const [menuOpen, setMenuOpen] = useState(false);
  const dark = useDarkTheme();
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);
  useEffect(() => setMenuOpen(false), [readingId]);
  /** 올리는 중인 그림 수 */
  const [uploading, setUploading] = useState(0);

  const load = useCallback(() => {
    return fetch('/api/notices', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!d?.success) { setError(d?.error || '공지를 불러오지 못했습니다'); return; }
        setNotices(d.notices);
        setCanWrite(!!d.canWrite);
        return d;
      })
      .catch(() => setError('공지를 불러오지 못했습니다'));
  }, []);

  useEffect(() => {
    load().then((d) => {
      if (!d) return;
      setSeenAt(d.seenAt ? new Date(d.seenAt).getTime() : 0);
      fetch('/api/notices/seen', { method: 'POST', credentials: 'include' })
        .finally(() => window.dispatchEvent(new CustomEvent('swpvpdb:notice-seen')));
    });
  }, [load]);

  useBackHandler(!!draft, () => setDraft(null));
  useBackHandler(!!readingId, () => setReadingId(null));
  // 창이 떠 있는 동안 뒤 페이지 스크롤 잠금 — 클래스로, 떠 있는 창 수를 세어 (hooks/useScrollLockClass — 인라인 값을 바꾸던 것이 목록의 잠금과 엉켜 풀리지 않았다)
  useScrollLockClass(!!draft || !!readingId);

  /** 그림을 서버에 올리고 글에 붙인다 — [그림 추가]로 고르거나 내용 칸에 붙여 넣기(Ctrl+V) */
  const addImages = (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length || !draft) return;
    const room = IMG_MAX - draft.images.length - uploading;
    if (room <= 0) { setError(`그림은 ${IMG_MAX}장까지입니다`); return; }
    setError('');
    for (const f of imgs.slice(0, room)) {
      setUploading((n) => n + 1);
      const fd = new FormData();
      fd.append('image', f);
      fetch('/api/notices/images', { method: 'POST', credentials: 'include', body: fd })
        .then((r) => r.json())
        .then((d) => {
          if (!d?.success) { setError(d?.error || '그림을 올리지 못했습니다'); return; }
          setDraft((cur) => (cur ? { ...cur, images: [...cur.images, { name: d.name, url: d.url }] } : cur));
        })
        .catch(() => setError('그림을 올리지 못했습니다'))
        .finally(() => setUploading((n) => n - 1));
    }
  };

  const save = () => {
    if (!draft) return;
    if (!draft.title.trim()) { setError('제목을 적어 주세요'); return; }
    setSaving(true);
    setError('');
    fetch(draft.id ? `/api/notices/${draft.id}` : '/api/notices', {
      method: draft.id ? 'PUT' : 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: draft.title, body: draft.body, pinned: draft.pinned, kakao: draft.kakao, images: draft.images.map((i) => i.name) })
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d?.success) { setError(d?.error || '저장하지 못했습니다'); return; }
        setDraft(null);
        return load();
      })
      .catch(() => setError('저장하지 못했습니다'))
      .finally(() => setSaving(false));
  };

  const remove = (n: Notice) => {
    if (!window.confirm(`"${n.title}" 공지를 지울까요?`)) return;
    fetch(`/api/notices/${n._id}`, { method: 'DELETE', credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (!d?.success) setError(d?.error || '지우지 못했습니다'); else setReadingId(null); return load(); })
      .catch(() => setError('지우지 못했습니다'));
  };

  /** 카톡 봇으로 다시 알린다(사용자 2026-10-27) — 보낼 방은 [설정] 탭의 '사이트 공지 알람' */
  const [resending, setResending] = useState(false);
  const resendKakao = (n: Notice) => {
    if (resending || !window.confirm('카톡으로 이 공지를 다시 알릴까요?')) return;
    setResending(true);
    fetch(`/api/notices/${n._id}/kakao`, { method: 'POST', credentials: 'include' })
      .then((r) => r.json())
      .then((d) => window.alert(d?.success ? '카톡으로 다시 알렸습니다.' : d?.error || '알리지 못했습니다'))
      .catch(() => window.alert('알리지 못했습니다'))
      .finally(() => setResending(false));
  };

  const togglePin = (n: Notice) => {
    fetch(`/api/notices/${n._id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: n.title, body: n.body, pinned: !n.pinned, images: n.images.map((i) => i.name) })
    })
      .then((r) => r.json())
      .then((d) => { if (!d?.success) setError(d?.error || '바꾸지 못했습니다'); return load(); })
      .catch(() => setError('바꾸지 못했습니다'));
  };

  // 다른 탭과 같은 짜임: 보라 그라데이션 바탕 · 흰 카드(둥글기 20) · 가운데 제목
  const pad = isMobile ? 'clamp(12px, 3vw, 16px)' : 20;
  const card: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)', borderRadius: 20, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    padding: pad, marginBottom: 14, textAlign: 'left'
  };
  const menuItem: React.CSSProperties = {
    padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 15,
    textAlign: 'left', color: '#333', fontWeight: 500, transition: 'all 0.2s ease', whiteSpace: 'nowrap'
  };

  const reading = readingId ? notices?.find((n) => n._id === readingId) || null : null;
  const isNewNotice = (n: Notice) => {
    const t = new Date(n.createdAt).getTime();
    return Date.now() - t < NEW_FOR_MS || (seenAt > 0 && t > seenAt);
  };
  /** 아이콘 칸은 글자 상자 가운데에 놓이는데 한글 글자 몸은 그보다 살짝 아래라 위로 떠 보였다(사용자 2026-10-27) — 조금 내린다 */
  const markStyle: React.CSSProperties = { width: 16, height: 16, flexShrink: 0, position: 'relative', top: 1.5 };
  const newMark = <img src="/img/notice-new.png" alt="새 공지" title="새 공지" style={markStyle} />;
  const C = PALETTE[dark ? 'dark' : 'light'];
  const paperCard: React.CSSProperties = { ...card, background: C.card };
  /**
   * 공지 한 편 — 팝업 안에 그린다(사용자 2026-10-27). 컴퓨터는 목록 위 가운데 창, 휴대폰은 화면을 꽉 채우는 창.
   * 위 줄은 보라 띠 — 관리 단추는 흰색, 컴퓨터는 오른쪽 끝에 닫기(×), 휴대폰은 왼쪽에 뒤로 가기.
   */
  const renderNotice = (n: Notice) => {
          const edited = new Date(n.updatedAt).getTime() - new Date(n.createdAt).getTime() > 60000;
          return (
            <div data-dark-skip="">
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: isMobile ? '12px 16px' : '10px 14px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: isMobile ? 'sticky' : 'static', top: 0, zIndex: 5
              }}>
                {/* 휴대폰 — 뒤로 가기. 보라 띠 위에 보라 동그라미를 또 얹으면 어색해서(사용자 2026-10-27) 반투명 흰 동그라미에 흰 화살표 */}
                {isMobile && <BackButton onClick={() => setReadingId(null)} label="목록" size={32} style={{ background: 'rgba(255, 255, 255, 0.22)', boxShadow: 'none', border: 'none' }} />}
                {/* 제목 — 보라 띠 안 (사용자 2026-10-28 "제목을 맨 위에다가 올려버리자" — 공지 글). 길면 말줄임, 마우스를 올리면 전체 */}
                <div title={n.title} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, paddingLeft: isMobile ? 2 : 12 }}>
                  {n.pinned && (
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, color: '#fff', background: 'rgba(255, 255, 255, 0.25)', borderRadius: 6, padding: '2px 7px' }}>공지</span>
                  )}
                  {/* 제목은 굵게 하지 않는다(사용자 2026-10-27) */}
                  <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{n.title}</span>
                  {isNewNotice(n) && <span style={{ flexShrink: 0, display: 'inline-flex' }}>{newMark}</span>}
                </div>
                {/* 관리 — 고정은 압정 그림(사용자 2026-10-27 초안 2), 고치기·지우기는 사이트의 다른 글과 같은 ⋮ 메뉴 */}
                {canWrite && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, position: 'relative' }}>
                    {/* 카톡으로 다시 알리기 — 노란 말풍선 */}
                    <button
                      title="카톡으로 다시 알리기"
                      aria-label="카톡으로 다시 알리기"
                      disabled={resending}
                      onClick={() => resendKakao(n)}
                      style={{
                        width: 34, height: 34, borderRadius: 8, border: 'none', padding: 0, cursor: 'pointer', background: 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: resending ? 0.5 : 1
                      }}
                    >
                      <KakaoIcon />
                    </button>
                    <button
                      title={n.pinned ? '고정 풀기' : '맨 위에 고정'}
                      aria-label={n.pinned ? '고정 풀기' : '맨 위에 고정'}
                      style={{
                        width: 34, height: 34, borderRadius: 8, border: 'none', padding: 0, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: n.pinned ? 'rgba(255, 255, 255, 0.22)' : 'transparent'
                      }}
                      onClick={() => togglePin(n)}
                    >
                      <PinIcon on={n.pinned} color="#fff" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                      title="더보기"
                      aria-label="더보기"
                      style={{
                        width: 34, height: 34, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#fff', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>⋮</span>
                    </button>
                    {menuOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute', top: 40, right: 0, minWidth: 70, zIndex: 100,
                          background: C.menu, border: `1px solid ${C.line}`, borderRadius: 12,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                        }}
                      >
                        <button
                          style={{ ...menuItem, color: C.menuText }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.14)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                          onClick={() => { setMenuOpen(false); setError(''); setDraft({ id: n._id, title: n.title, body: n.body, pinned: n.pinned, kakao: false, images: n.images }); }}
                        >수정</button>
                        <button
                          style={{ ...menuItem, color: dark ? '#ff8a80' : '#d32f2f' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(211, 47, 47, 0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                          onClick={() => { setMenuOpen(false); remove(n); }}
                        >삭제</button>
                      </div>
                    )}
                  </div>
                )}
                {!isMobile && (
                  <button
                    aria-label="닫기"
                    title="닫기"
                    onClick={() => setReadingId(null)}
                    style={{ width: 34, height: 34, borderRadius: 8, border: 'none', background: 'transparent', color: '#fff', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >×</button>
                )}
              </div>
              <div style={{ padding: isMobile ? '18px 16px 28px' : '20px 26px 26px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.sub }}>
                {/* 작성자 프사는 네모(사용자 2026-10-27) */}
                <img
                  src={n.authorProfileImg || '/images/default-profile.png'}
                  alt=""
                  style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover' }}
                />
                <span style={{ fontWeight: 700, color: C.name }}>{n.authorName}</span>
                <span>· {getRelativeTime(n.createdAt)}{edited ? ' (수정됨)' : ''}</span>
              </div>
              {/* 본문 — 사진이 있으면 본문 맨 위에, 여러 장은 옆으로 넘기는 묶음(사용자 2026-10-27) */}
              {(n.body || n.images.length > 0) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
                  {n.images.length > 0 && (
                    <div style={{ marginBottom: n.body ? 12 : 0 }}>
                      <PhotoAlbum photos={n.images} />
                    </div>
                  )}
                  {n.body && (
                    <div style={{ fontSize: 14, lineHeight: 1.7, color: C.body, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.body}</div>
                  )}
                </div>
              )}
              </div>
            </div>
          );
  };

  /** 번호 · 제목 · 글쓴이 · 날짜 */
  const cols = isMobile ? '34px minmax(0, 1fr) 68px 38px' : '48px minmax(0, 1fr) 120px 64px';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? 'clamp(12px, 3vw, 20px) clamp(6px, 2vw, 12px)' : '32px 20px'
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 'clamp(18px, 4vw, 22px)' : 26, fontWeight: 800, color: PURPLE }}>공지</h2>
        </div>

        {error && <div style={{ ...card, color: '#d32f2f', fontSize: 14, textAlign: 'center' }}>{error}</div>}

        {!notices && !error && <div style={{ ...card, color: '#888', fontSize: 14, textAlign: 'center' }}>불러오는 중…</div>}
        {notices && !notices.length && !draft && !reading && (
          <div style={{ ...card, color: '#888', fontSize: 14, textAlign: 'center' }}>아직 공지가 없습니다</div>
        )}

        {notices && notices.length > 0 && (
          // 게시판 줄 — 번호 · 제목 · 작성자 · 작성일. 고정은 번호 대신 '공지', 옅은 바탕
          <div data-dark-skip="" style={{ ...paperCard, padding: isMobile ? '8px 10px' : '10px 16px' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: cols, gap: 8, padding: '6px 4px',
              fontSize: 12, color: C.head, fontWeight: 700, borderBottom: `1.5px solid ${C.headLine}`
            }}>
              <span style={{ textAlign: 'center' }}>번호</span><span>제목</span><span>작성자</span><span style={{ textAlign: 'right' }}>작성일</span>
            </div>
            {notices.map((n, i) => {
              const no = notices.filter((x) => !x.pinned).length - notices.slice(0, i).filter((x) => !x.pinned).length;
              return (
                <div
                  key={n._id}
                  onClick={() => { setReadingId(n._id); window.scrollTo({ top: 0 }); }}
                  style={{
                    display: 'grid', gridTemplateColumns: cols, gap: 8, padding: isMobile ? '10px 4px' : '11px 4px', alignItems: 'center',
                    borderBottom: i < notices.length - 1 ? `1px solid ${C.line}` : 'none',
                    background: n.pinned ? C.pinBg : 'transparent', cursor: 'pointer'
                  }}
                >
                  <span style={{ textAlign: 'center' }}>
                    {n.pinned
                      ? <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 800, color: '#fff', background: C.badge, borderRadius: 5, padding: isMobile ? '2px 4px' : '2px 5px', whiteSpace: 'nowrap' }}>공지</span>
                      : <span style={{ fontSize: 12, color: C.sub }}>{no}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                    <span style={{
                      fontSize: isMobile ? 13 : 14, fontWeight: 400, color: C.title,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>{n.title}</span>
                    {/* 사진 붙은 글·새 글 표시 — 사용자가 준 그림 그대로(public/img/notice-*.png, 사용자 2026-10-27) */}
                    {n.images.length > 0 && <img src="/img/notice-photo.png" alt="사진" title="사진" style={markStyle} />}
                    {isNewNotice(n) && newMark}
                  </span>
                  {/* 작성자 — 왼쪽에 네모 프사(사용자 2026-10-27) */}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                    <img
                      src={n.authorProfileImg || '/images/default-profile.png'}
                      alt=""
                      style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 12, color: C.name, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.authorName}</span>
                  </span>
                  <span style={{ fontSize: 12, color: C.sub, textAlign: 'right', whiteSpace: 'nowrap' }}>{shortDate(n.createdAt)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/*
        공지를 누르면 팝업(사용자 2026-10-27) — 컴퓨터는 목록 위 가운데 창(바깥·×·뒤로 가기로 닫음),
        휴대폰은 화면을 꽉 채우는 창(뒤로 가기로 닫음, 공략 창과 같은 짜임)
      */}
      {reading && createPortal(
        <div
          onClick={() => setReadingId(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2400, background: isMobile ? (dark ? C.card : '#ffffff') : 'rgba(0, 0, 0, 0.5)',
            backdropFilter: isMobile ? 'none' : 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={isMobile ? 'fullscreen-scroll' : undefined}
            style={{
              overflowY: 'auto', background: dark ? C.card : '#ffffff',
              ...(isMobile
                // 휴대폰은 전체 화면 — border-radius 를 적지 않는다(index.css 의 팝업 보라 테두리가 붙지 않게, 사용자 2026-10-28)
                ? { width: '100%', height: '100%' }
                : { width: 'min(680px, 92vw)', maxHeight: '90vh', borderRadius: 24, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(102, 126, 234, 0.15)' })
            }}
          >
            {renderNotice(reading)}
          </div>
        </div>,
        document.body
      )}

      {/*
        **글쓰기 창**(사용자 2026-10-27 초안 4 · 사이트 색) — 따로 띄운다. 컴퓨터는 어두운 바탕 위 가운데 창, 휴대폰은 화면을 꽉 채운다.
        보라 그라데이션 머리띠 · 다크모드는 공지 화면과 같은 어두운 카드. body 로 띄워 탭 틀(transform)·다크모드 자동 칠하기 밖에 둔다.
        바깥을 눌러도 닫지 않는다 — 쓰던 글이 날아가지 않게. ×·← ·뒤로 가기·[취소] 로 닫는다.
      */}
      {draft && createPortal((() => {
        const K = dark
          ? { panel: '#1f1d33', page: '#1f1d33', field: '#2a2745', fieldLine: '#3a3660', text: '#f1efff', label: '#c8c4ec', accent: '#8b80ff', dash: '#4a4580', overlay: 'rgba(10, 8, 25, 0.6)' }
          : { panel: '#ffffff', page: '#f6f7fd', field: '#ffffff', fieldLine: '#e0e0e0', text: '#222', label: '#4b5375', accent: '#667eea', dash: '#667eea', overlay: 'rgba(30, 28, 60, 0.55)' };
        const GRAD = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        /* 테두리는 공략 추가 창과 같게(사용자 2026-10-27) — 회색 2px, 누르면 보라 + 옅은 빛 */
        const field: React.CSSProperties = {
          width: '100%', boxSizing: 'border-box', border: `2px solid ${K.fieldLine}`, borderRadius: 12, background: K.field,
          color: K.text, padding: '12px 16px', fontSize: 15, outline: 'none', fontFamily: 'inherit', transition: 'all 0.3s ease'
        };
        const focusOn = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = K.accent;
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
        };
        const focusOff = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          e.currentTarget.style.borderColor = K.fieldLine;
          e.currentTarget.style.boxShadow = 'none';
        };
        const thumb = isMobile ? 52 : 58;
        const check = (on: boolean, set: (v: boolean) => void, label: string) => (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: K.label, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} style={{ accentColor: K.accent, width: 15, height: 15, margin: 0 }} />
            {label}
          </label>
        );
        const busy = saving || uploading > 0;
        const submit = (
          <button onClick={save} disabled={busy} style={{
            border: 'none', borderRadius: 10, background: GRAD, color: '#fff', fontWeight: 800, cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', opacity: busy ? 0.6 : 1,
            ...(isMobile ? { width: '100%', padding: '13px 0', fontSize: 15 } : { padding: '9px 18px', fontSize: 13 })
          }}>{saving ? '저장 중…' : draft.id ? '고치기' : '올리기'}</button>
        );
        return (
          <div style={{
            // inset·100vw 대신 width/height 100% — index.css 의 '팝업 공통 보라 테두리'(inset: 0 · 100vw 바탕 안의 둥근 상자)에
            // 걸리지 않게(사용자 2026-10-27 "테두리 없애"). 네 변을 0 으로 주면 브라우저가 inset: 0 으로 적어 걸린다
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2500, background: isMobile ? K.page : K.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 24
          }}>
            <div style={{
              background: isMobile ? K.page : K.panel, display: 'flex', flexDirection: 'column', overflow: 'hidden',
              ...(isMobile
                ? { width: '100%', height: '100%' }
                : { width: 'min(640px, 100%)', maxHeight: '90vh', borderRadius: 24, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(102, 126, 234, 0.15)' })
            }}>
              {/* 머리띠 — 컴퓨터는 제목 · ×, 휴대폰은 ← · 가운데 제목 */}
              <div style={{
                background: GRAD, color: '#fff', padding: isMobile ? '12px 14px' : '13px 16px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 16, fontWeight: 800
              }}>
                {isMobile && (
                  <button aria-label="닫기" onClick={() => setDraft(null)} style={{ border: 'none', background: 'none', color: '#fff', fontSize: 22, padding: 0, width: 28, cursor: 'pointer', lineHeight: 1 }}>←</button>
                )}
                <span>{draft.id ? '공지 고치기' : '새 공지'}</span>
                {isMobile
                  ? <span style={{ width: 28 }} />
                  : <button aria-label="닫기" onClick={() => setDraft(null)} style={{ border: 'none', background: 'none', color: '#fff', fontSize: 24, padding: 0, cursor: 'pointer', lineHeight: 1, fontWeight: 400 }}>×</button>}
              </div>

              <div style={{ padding: isMobile ? 12 : '14px 16px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {error && <div style={{ color: dark ? '#ff8a80' : '#d32f2f', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{error}</div>}
                <input
                  value={draft.title}
                  maxLength={100}
                  placeholder="제목"
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  onFocus={focusOn}
                  onBlur={focusOff}
                  style={{ ...field, fontWeight: 500, marginBottom: 10, flexShrink: 0 }}
                />
                <textarea
                  value={draft.body}
                  maxLength={5000}
                  placeholder="내용"
                  rows={10}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  onPaste={(e) => {
                    const files = Array.from(e.clipboardData.files || []);
                    if (files.some((f) => f.type.startsWith('image/'))) { e.preventDefault(); addImages(files); }
                  }}
                  onFocus={focusOn}
                  onBlur={focusOff}
                  style={{ ...field, borderRadius: 16, lineHeight: 1.7, resize: isMobile ? 'none' : 'vertical', ...(isMobile ? { flex: 1, minHeight: 150 } : {}) }}
                />
                {/* 붙인 그림 — 작은 그림의 × 를 누르면 뺀다. 내용 칸에 붙여 넣어도(Ctrl+V) 올라간다 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginTop: 10, alignItems: 'center', flexShrink: 0 }}>
                  {draft.images.map((img) => (
                    <div key={img.name} style={{ position: 'relative', width: thumb, height: thumb }}>
                      <img src={img.url} alt="" style={{ width: thumb, height: thumb, objectFit: 'cover', borderRadius: 8, border: `2px solid ${K.fieldLine}`, boxSizing: 'border-box' }} />
                      <button
                        aria-label="그림 빼기"
                        onClick={() => setDraft({ ...draft, images: draft.images.filter((x) => x.name !== img.name) })}
                        style={{
                          position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none',
                          background: '#555', color: '#fff', fontSize: 12, lineHeight: '20px', padding: 0, cursor: 'pointer'
                        }}
                      >×</button>
                    </div>
                  ))}
                  {Array.from({ length: uploading }).map((_, i) => (
                    <div key={`up-${i}`} style={{
                      width: thumb, height: thumb, borderRadius: 8, border: `1.5px dashed ${K.dash}`, boxSizing: 'border-box',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: K.label
                    }}>올리는 중</div>
                  ))}
                  {draft.images.length + uploading < IMG_MAX && (
                    <label style={{
                      width: thumb, height: thumb, borderRadius: 8, border: `1.5px dashed ${K.dash}`, boxSizing: 'border-box',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                      color: K.accent, fontSize: 10, fontWeight: 700, cursor: 'pointer', background: dark ? 'transparent' : '#f0f0f0'
                    }}>
                      <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
                      {isMobile ? '그림' : '그림 추가'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => { addImages(Array.from(e.target.files || [])); e.target.value = ''; }}
                      />
                    </label>
                  )}
                </div>

                {isMobile ? (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 9, flexShrink: 0 }}>
                    {check(draft.pinned, (v) => setDraft({ ...draft, pinned: v }), '맨 위에 고정')}
                    {!draft.id && check(draft.kakao, (v) => setDraft({ ...draft, kakao: v }), '카톡으로 알리기')}
                    <div style={{ marginTop: 4 }}>{submit}</div>
                  </div>
                ) : (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexShrink: 0 }}>
                    <div style={{ display: 'flex', gap: 16 }}>
                      {check(draft.pinned, (v) => setDraft({ ...draft, pinned: v }), '맨 위에 고정')}
                      {!draft.id && check(draft.kakao, (v) => setDraft({ ...draft, kakao: v }), '카톡으로 알리기')}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setDraft(null)} disabled={saving} style={{
                        borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                        border: `1px solid ${dark ? '#3a3660' : 'rgba(102, 126, 234, 0.35)'}`,
                        background: dark ? 'transparent' : 'rgba(102, 126, 234, 0.06)', color: dark ? '#c8c4ec' : '#667eea'
                      }}>취소</button>
                      {submit}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* 공지 쓰기 — 공략 탭의 [+] 와 같은 오른쪽 아래 둥근 단추(사용자 2026-10-27). 길마·부길마만 */}
      {canWrite && !draft && (
        <button
          onClick={() => { setError(''); setDraft({ id: null, title: '', body: '', pinned: false, kakao: true, images: [] }); }}
          style={{
            position: 'fixed',
            right: isMobile ? 'clamp(16px, 3vw, 20px)' : 40,
            bottom: isMobile ? 'clamp(16px, 3vw, 20px)' : 40,
            width: isMobile ? 'clamp(48px, 9vw, 52px)' : 64,
            height: isMobile ? 'clamp(48px, 9vw, 52px)' : 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b9eff 0%, #a78bfa 100%)',
            color: '#fff',
            border: 'none',
            fontSize: isMobile ? 'clamp(20px, 4vw, 24px)' : 28,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(139, 158, 255, 0.5)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            if (isMobile) return;
            e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(139, 158, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1) translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 158, 255, 0.5)';
          }}
          title="공지 쓰기"
          aria-label="공지 쓰기"
        >
          +
        </button>
      )}
    </div>
  );
};

export default NoticePage;
